// Каталог тем оформления. Сами палитры живут в src/styles/themes.css —
// здесь только то, что нужно интерфейсу: порядок на ползунке, светлая тема
// или тёмная, и пара цветов для полоски выбора.
//
// Названия тем не переводятся: это имена палитр, такие же собственные, как
// названия шрифтов. Переводится только подпись «светлая/тёмная» рядом с ними.

export const THEME_IDS = [
  'linen',
  'terrazzo',
  'matcha',
  'riviera',
  'iris',
  'liquidlight',
  'liquiddark',
  'darkula',
  'ember',
  'lagoon',
  'nocturne',
  'obsidian',
] as const;

export type ThemeId = (typeof THEME_IDS)[number];

export type ThemeMode = 'light' | 'dark';

export interface ThemeMeta {
  id: ThemeId;
  /** Собственное имя палитры, одинаковое во всех языках. */
  name: string;
  mode: ThemeMode;
  /**
   * Фон и акцент — из них собирается дорожка ползунка. `bg` уезжает в
   * `background`, поэтому это не обязательно цвет: у стеклянных тем там
   * градиент, и полоска на ползунке сразу показывает, чем они отличаются.
   */
  swatch: { bg: string; accent: string };
}

export const THEMES: Record<ThemeId, ThemeMeta> = {
  linen: {
    id: 'linen',
    name: 'Linen',
    mode: 'light',
    swatch: { bg: '#f6f6f4', accent: '#2f6f8f' },
  },
  terrazzo: {
    id: 'terrazzo',
    name: 'Terrazzo',
    mode: 'light',
    swatch: { bg: '#faf4ee', accent: '#a2603a' },
  },
  matcha: {
    id: 'matcha',
    name: 'Matcha',
    mode: 'light',
    swatch: { bg: '#f3f5ef', accent: '#3f7a4a' },
  },
  riviera: {
    id: 'riviera',
    name: 'Riviera',
    mode: 'light',
    swatch: { bg: '#eef4fb', accent: '#1470dd' },
  },
  iris: {
    id: 'iris',
    name: 'Iris',
    mode: 'light',
    swatch: { bg: '#f4f3fb', accent: '#4f46e5' },
  },
  // Liquid Glass — одна тема в двух режимах, поэтому имя у них общее, а
  // различает их подпись «светлая/тёмная» рядом. На ползунке они соседи и
  // стоят на стыке светлой и тёмной половин.
  liquidlight: {
    id: 'liquidlight',
    name: 'Liquid Glass',
    mode: 'light',
    swatch: { bg: 'linear-gradient(140deg, #d2e7fa, #e9f0f9 48%, #d8f0e9)', accent: '#0a6478' },
  },
  liquiddark: {
    id: 'liquiddark',
    name: 'Liquid Glass',
    mode: 'dark',
    swatch: { bg: 'linear-gradient(140deg, #131f4a, #060818 48%, #0c2e35)', accent: '#5eead4' },
  },
  darkula: {
    id: 'darkula',
    name: 'Darkula',
    mode: 'dark',
    swatch: { bg: '#2b2b2b', accent: '#cc9a54' },
  },
  ember: {
    id: 'ember',
    name: 'Ember',
    mode: 'dark',
    swatch: { bg: '#191512', accent: '#dfa15e' },
  },
  lagoon: {
    id: 'lagoon',
    name: 'Lagoon',
    mode: 'dark',
    swatch: { bg: '#0b1c22', accent: '#06b6d4' },
  },
  nocturne: {
    id: 'nocturne',
    name: 'Nocturne',
    mode: 'dark',
    swatch: { bg: '#131426', accent: '#b48ce8' },
  },
  obsidian: {
    id: 'obsidian',
    name: 'Obsidian',
    mode: 'dark',
    swatch: { bg: '#0f1113', accent: '#4f9df7' },
  },
};

/** Тема по умолчанию для тех, кто ничего не выбирал и не просил тёмную:
    светлая половина Liquid Glass — витрина редизайна. */
export const DEFAULT_THEME: ThemeId = 'liquidlight';

/** Что показываем, если система просит тёмное оформление: тёмная половина
    той же пары — дефолт следует за системной схемой, а не меняет материал. */
export const DEFAULT_DARK_THEME: ThemeId = 'liquiddark';

export function isThemeId(value: unknown): value is ThemeId {
  return typeof value === 'string' && (THEME_IDS as readonly string[]).includes(value);
}

/**
 * Ступени размера шрифта: множитель к шкале --fs-*, шаг ровно 10%.
 * Вверх есть куда расти вплоть до 150% — интерфейс плотный, и на большом
 * экране или с плохим зрением одного-двух шагов не хватало. Вниз ступеней две:
 * мельче 90% плотная сетка становится нечитаемой.
 */
export const FONT_SCALES = [0.9, 1, 1.1, 1.2, 1.3, 1.4, 1.5] as const;

/** Ступень по умолчанию — 110%: на ней интерфейс читается без прищура. */
export const DEFAULT_FONT_SCALE_INDEX = 2;

export const DEFAULT_FONT_SCALE = FONT_SCALES[DEFAULT_FONT_SCALE_INDEX];

/** Подписи ступеней размера — ключи переводов, по одному на ступень. */
export const FONT_SCALE_LABEL_KEYS = [
  'person.size.compact',
  'person.size.dense',
  'person.size.default',
  'person.size.large',
  'person.size.larger',
  'person.size.xlarge',
  'person.size.huge',
] as const;

/**
 * Ступени отступов: множитель ко всей шкале --sp-*. Диапазон шире, чем у
 * кегля — от совсем тесной сетки, где на экран влезает максимум вариантов,
 * до просторной, где блоки дышат.
 */
export const SPACE_SCALES = [0.7, 0.85, 1, 1.2, 1.4, 1.6] as const;

/** Ступень по умолчанию — плотность, под которую нарисован интерфейс. */
export const DEFAULT_SPACE_SCALE_INDEX = 2;

export const DEFAULT_SPACE_SCALE = SPACE_SCALES[DEFAULT_SPACE_SCALE_INDEX];

export const SPACE_SCALE_LABEL_KEYS = [
  'person.space.tight',
  'person.space.dense',
  'person.space.default',
  'person.space.roomy',
  'person.space.wide',
  'person.space.max',
] as const;

/** Ближайшая ступень отступов — та же защита от мусора в хранилище. */
export function nearestSpaceScaleIndex(value: unknown): number {
  return nearestIndex(SPACE_SCALES, value, DEFAULT_SPACE_SCALE_INDEX);
}

/**
 * Ближайшая известная ступень к произвольному числу. Хранилище правит руками,
 * версии ступеней со временем меняются — попадать надо всегда во что-то валидное.
 */
function nearestIndex(scales: readonly number[], value: unknown, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  let best = fallback;
  for (let i = 0; i < scales.length; i += 1) {
    if (Math.abs(scales[i] - value) < Math.abs(scales[best] - value)) best = i;
  }
  return best;
}

export function nearestFontScaleIndex(value: unknown): number {
  return nearestIndex(FONT_SCALES, value, DEFAULT_FONT_SCALE_INDEX);
}
